import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Button, IconArrowLeft, ListTable, Notice, Plate, TextField, type ListColumn } from "@wilco/ui";
import type { ReactNode } from "react";
import { DocumentList, type ListQuery } from "../docs/DocumentList.tsx";
import { FormPage, useDocumentForm } from "../docs/DocumentForm.tsx";
import { formatDate, formatDateTime, formatMoney, serverErrorText, t } from "../i18n.ts";
import { errorNotice } from "../stock/ShelfPage.tsx";
import { TxBadge } from "./AccountPage.tsx";
import { accountsQuery, docsApi, MONEY, type Account, type Statement, type Transaction, type TxState } from "./api.ts";

/*
 * «Операції» банка (каркас #77): списки счетов, платежей и загрузок выписки,
 * карточки и формы. Страницы, поиск и состояние считает сервер (CursorPage),
 * экран только передаёт их. Рабочий экран «Банк» (/money/bank) остаётся
 * местом разбора «Не зіставлено» и загрузки файла.
 */

const STATES: TxState[] = ["PENDING", "SUPPLIER_PAYMENT", "CUSTOMER_RECEIPT", "OPEX", "OWN_TRANSFER", "IGNORED"];
const BY_DATE = [{ value: "date", label: t("money.sort.newest") }];

const accountName = (accounts: Account[] | undefined, id: number) => accounts?.find((a) => a.id === id)?.name ?? String(id);

// --- счета -------------------------------------------------------------------

const accountsRoute = getRouteApi("/app/money/bank/accounts");

export function AccountsListPage() {
  const search = accountsRoute.useSearch();
  const navigate = useNavigate();
  const columns: ListColumn<Account>[] = [
    {
      key: "name",
      header: t("bank.accountName"),
      kind: "title",
      render: (a) => a.name,
    },
    {
      key: "iban",
      header: t("bank.iban"),
      kind: "text",
      render: (a) => <span className="w-num">{a.iban}</span>,
    },
    {
      key: "source",
      header: t("money.source"),
      kind: "text",
      width: "10rem",
      render: (a) => t(a.api ? "bank.api" : "bank.file"),
    },
    {
      key: "balance",
      header: t("money.balance"),
      kind: "number",
      width: "11rem",
      render: (a) => (a.balance == null ? t("bank.noBalance") : formatMoney(a.balance, a.currency)),
    },
    {
      key: "error",
      header: t("money.lastError"),
      kind: "text",
      width: "12rem",
      render: (a) => (a.lastError ? serverText(a.lastError) : ""),
    },
  ];
  return (
    <DocumentList
      title={t("ops.bank.accounts")}
      icon="expense"
      queryKey={[...MONEY, "docs", "accounts"]}
      load={({ q, cursor, limit }: ListQuery) => docsApi.accounts(q, { cursor, limit })}
      columns={columns}
      rowKey={(a) => String(a.id)}
      onOpen={(a) =>
        void navigate({
          to: "/money/bank/accounts/$id",
          params: { id: String(a.id) },
        })
      }
      sorts={[{ value: "name", label: t("money.sort.name") }]}
      search={search}
      onSearch={(next) =>
        void navigate({
          to: "/money/bank/accounts",
          search: next,
          replace: true,
        })
      }
      searchHint={t("money.accounts.searchHint")}
      create={{ label: t("bank.newAccount"), to: "/money/bank/accounts/new" }}
      empty={{ title: t("bank.accounts.empty") }}
    />
  );
}

/** Код ошибки банка словами каталога ошибок, как в карточке счёта. */
const serverText = (code: string) => serverErrorText(code) ?? code;

interface AccountValues {
  iban: string;
  name: string;
  currency: string;
  tokenSecret: string;
}

export function NewAccountPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useDocumentForm<AccountValues, Account>({
    initial: { iban: "", name: "", currency: "UAH", tokenSecret: "" },
    validate: (v) => ({
      iban: v.iban.trim() ? undefined : t("docs.required"),
      name: v.name.trim() ? undefined : t("docs.required"),
    }),
    submit: (v, key) =>
      docsApi.createAccount(
        {
          iban: v.iban,
          name: v.name,
          currency: v.currency || "UAH",
          tokenSecret: v.tokenSecret.trim() || null,
        },
        key,
      ),
    onSuccess: (a) => {
      void queryClient.invalidateQueries({ queryKey: MONEY });
      void navigate({
        to: "/money/bank/accounts/$id",
        params: { id: String(a.id) },
        replace: true,
      });
    },
    fieldOf: (code) =>
      code === "banking.iban_invalid" || code === "banking.account_exists" ? "iban" : code === "banking.currency_invalid" ? "currency" : undefined,
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={t("bank.newAccount")} back="/money/bank/accounts" submitLabel={t("bank.addAccount")} form={form}>
      <TextField label={t("bank.iban")} value={values.iban} onChange={(e) => set("iban", e.target.value)} error={errors.iban} autoComplete="off" />
      <TextField label={t("bank.accountName")} value={values.name} onChange={(e) => set("name", e.target.value)} error={errors.name} />
      <TextField label={t("bank.currency")} value={values.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} error={errors.currency} />
      <TextField
        label={t("bank.tokenSecret")}
        value={values.tokenSecret}
        onChange={(e) => set("tokenSecret", e.target.value)}
        hint={t("bank.tokenSecretHint")}
      />
    </FormPage>
  );
}

const editAccountRoute = getRouteApi("/app/money/bank/accounts/$id/edit");

/** Правка счёта: имя и секрет токена. IBAN и валюта не правятся: по ним разобраны платежи. */
export function EditAccountPage() {
  const { id } = editAccountRoute.useParams();
  const account = useQuery({
    queryKey: [...MONEY, "account", id],
    queryFn: () => docsApi.account(Number(id)),
  });
  if (account.error) return <Notice tone="danger" title={errorNotice(account.error).title} />;
  if (!account.data) return null;
  return <EditAccountForm account={account.data} />;
}

function EditAccountForm({ account }: { account: Account }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = String(account.id);
  const form = useDocumentForm<{ name: string; tokenSecret: string }, Account>({
    initial: { name: account.name, tokenSecret: "" },
    validate: (v) => ({ name: v.name.trim() ? undefined : t("docs.required") }),
    // Секрет токена сервер не отдаёт: пустое поле оставляет счёт без API, как при создании.
    submit: (v, key) => docsApi.updateAccount(account.id, { name: v.name, tokenSecret: v.tokenSecret.trim() || null }, key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MONEY });
      void navigate({
        to: "/money/bank/accounts/$id",
        params: { id },
        replace: true,
      });
    },
    fieldOf: (code) => (code === "banking.name_required" ? "name" : undefined),
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={account.name} back="/money/bank/accounts" submitLabel={t("money.save")} form={form}>
      <p className="w-muted w-num">
        {account.iban} · {account.currency}
      </p>
      <TextField label={t("bank.accountName")} value={values.name} onChange={(e) => set("name", e.target.value)} error={errors.name} />
      <TextField
        label={t("bank.tokenSecret")}
        value={values.tokenSecret}
        onChange={(e) => set("tokenSecret", e.target.value)}
        hint={t(account.api ? "money.tokenSecretKeep" : "bank.tokenSecretHint")}
      />
    </FormPage>
  );
}

// --- платежи -----------------------------------------------------------------

const paymentsRoute = getRouteApi("/app/money/bank/payments");

export function PaymentsListPage() {
  const search = paymentsRoute.useSearch();
  const navigate = useNavigate();
  const accounts = useQuery(accountsQuery);
  return (
    <DocumentList
      title={t("ops.bank.payments")}
      icon="expense"
      queryKey={[...MONEY, "docs", "payments"]}
      load={({ q, status, cursor, limit }: ListQuery) => docsApi.payments(q, status, { cursor, limit })}
      columns={paymentColumns(accounts.data)}
      rowKey={(r) => String(r.id)}
      onOpen={(r) =>
        void navigate({
          to: "/money/bank/payments/$id",
          params: { id: String(r.id) },
        })
      }
      statuses={STATES.map((s) => ({ value: s, label: t(`bank.state.${s}`) }))}
      sorts={BY_DATE}
      search={search}
      onSearch={(next) =>
        void navigate({
          to: "/money/bank/payments",
          search: next,
          replace: true,
        })
      }
      searchHint={t("money.payments.searchHint")}
      empty={{
        title: t("money.payments.empty"),
        body: t("money.payments.emptyBody"),
      }}
    />
  );
}

const paymentColumns = (accounts: Account[] | undefined): ListColumn<Transaction>[] => [
  {
    key: "who",
    header: t("bank.counterparty"),
    kind: "title",
    render: (r) => r.counterparty || t("bank.noCounterparty"),
  },
  {
    key: "purpose",
    header: t("bank.purpose"),
    kind: "text",
    render: (r) => r.purpose,
  },
  {
    key: "account",
    header: t("money.account"),
    kind: "text",
    width: "10rem",
    render: (r) => accountName(accounts, r.account),
  },
  {
    key: "date",
    header: t("bank.date"),
    kind: "text",
    width: "6rem",
    render: (r) => formatDate(r.date),
  },
  {
    key: "state",
    header: t("bank.state"),
    kind: "text",
    width: "10rem",
    render: (r) => <TxBadge state={r.state} />,
  },
  {
    key: "amount",
    header: t("bank.amount"),
    kind: "number",
    width: "10rem",
    render: (r) => formatMoney(r.amount, r.currency),
  },
];

const paymentRoute = getRouteApi("/app/money/bank/payments/$id");

/** Карточка платежа: откуда пришёл, чем стал, кто разобрал. Неразобранный ведёт на «Не зіставлено». */
export function PaymentPage() {
  const { id } = paymentRoute.useParams();
  const tx = useQuery({
    queryKey: [...MONEY, "payment", id],
    queryFn: () => docsApi.payment(Number(id)),
  });
  const accounts = useQuery(accountsQuery);
  const r = tx.data;
  return (
    <div className="page stock-narrow money">
      <BackHead to="/money/bank/payments" title={r ? r.counterparty || t("bank.noCounterparty") : t("money.payment")} />
      {tx.error ? <Notice tone="danger" title={errorNotice(tx.error).title} /> : null}
      {r ? (
        <Plate cut="lg">
          <dl className="money-facts">
            <Fact label={t("bank.amount")}>
              <span className="w-num">{formatMoney(r.amount, r.currency)}</span>
              {r.uahEquivalent != null && r.currency !== "UAH" ? <span className="w-muted"> · {formatMoney(r.uahEquivalent, "UAH")}</span> : null}
            </Fact>
            <Fact label={t("bank.date")}>{formatDate(r.date)}</Fact>
            <Fact label={t("money.account")}>
              <Link to="/money/bank/accounts/$id" params={{ id: String(r.account) }}>
                {accountName(accounts.data, r.account)}
              </Link>
            </Fact>
            <Fact label={t("bank.purpose")}>{r.purpose}</Fact>
            {r.edrpou ? <Fact label={t("money.edrpou")}>{r.edrpou}</Fact> : null}
            {r.iban ? <Fact label={t("bank.iban")}>{r.iban}</Fact> : null}
            <Fact label={t("bank.state")}>
              <TxBadge state={r.state} />
            </Fact>
            {r.target ? <Fact label={t("money.target")}>{targetText(r.target)}</Fact> : null}
            {r.note ? <Fact label={t("money.note")}>{r.note}</Fact> : null}
            <Fact label={t("money.resolvedBy")}>{r.resolvedBy ?? (r.state === "PENDING" ? "" : t("money.resolvedAuto"))}</Fact>
            {r.statement ? (
              <Fact label={t("money.statement")}>
                <Link to="/money/bank/statements/$id" params={{ id: String(r.statement) }}>
                  {t("money.statementNo", { id: r.statement })}
                </Link>
              </Fact>
            ) : null}
          </dl>
          {r.state === "PENDING" ? (
            <Button asChild variant="primary">
              <Link to="/money/bank" search={{ tab: "pending" }}>
                {t("bank.resolve")}
              </Link>
            </Button>
          ) : null}
        </Plate>
      ) : null}
    </div>
  );
}

/** Куда разнесён платёж: запись «Витрат» ссылкой, заказ номером как есть. */
function targetText(target: string) {
  const entry = /^opex\/(\d+)$/.exec(target);
  if (entry?.[1]) {
    return (
      <Link to="/money/opex/entries/$id" params={{ id: entry[1] }}>
        {t("money.entryNo", { id: entry[1] })}
      </Link>
    );
  }
  return target;
}

// --- выписки -----------------------------------------------------------------

const statementsRoute = getRouteApi("/app/money/bank/statements");

/**
 * Журнал загрузок выписки, новые сверху. Счёт выбирается чипом (параметр
 * account сервера); текстового поиска у журнала нет, поле ищет по счёту и
 * источнику на загруженных страницах.
 */
export function StatementsListPage() {
  const search = statementsRoute.useSearch();
  const navigate = useNavigate();
  const accounts = useQuery(accountsQuery);
  const columns: ListColumn<Statement>[] = [
    {
      key: "at",
      header: t("money.loadedAt"),
      kind: "title",
      width: "11rem",
      render: (s) => formatDateTime(s.createdAt),
    },
    {
      key: "account",
      header: t("money.account"),
      kind: "text",
      render: (s) => accountName(accounts.data, s.account),
    },
    {
      key: "source",
      header: t("money.source"),
      kind: "text",
      width: "9rem",
      render: (s) => t(s.source === "API" ? "bank.api" : "bank.file"),
    },
    {
      key: "period",
      header: t("money.period"),
      kind: "text",
      width: "12rem",
      render: (s) => period(s),
    },
    {
      key: "result",
      header: t("money.result"),
      kind: "text",
      width: "14rem",
      render: (s) => result(s),
    },
  ];
  return (
    <DocumentList
      title={t("ops.bank.statements")}
      icon="expense"
      queryKey={[...MONEY, "docs", "statements"]}
      load={async ({ q, status, cursor, limit }: ListQuery) => {
        const page = await docsApi.statements(status ? Number(status) : null, {
          cursor,
          limit,
        });
        const needle = q.toLocaleLowerCase("uk");
        const text = (s: Statement) => `${accountName(accounts.data, s.account)} ${s.source} ${s.format ?? ""} ${s.error ?? ""}`.toLocaleLowerCase("uk");
        return needle
          ? {
              ...page,
              items: page.items.filter((s) => text(s).includes(needle)),
            }
          : page;
      }}
      columns={columns}
      rowKey={(s) => String(s.id)}
      onOpen={(s) =>
        void navigate({
          to: "/money/bank/statements/$id",
          params: { id: String(s.id) },
        })
      }
      statuses={(accounts.data ?? []).map((a) => ({
        value: String(a.id),
        label: a.name,
      }))}
      sorts={BY_DATE}
      search={search}
      onSearch={(next) =>
        void navigate({
          to: "/money/bank/statements",
          search: next,
          replace: true,
        })
      }
      searchHint={t("money.statements.searchHint")}
      create={{ label: t("bank.upload"), to: "/money/bank" }}
      empty={{
        title: t("money.statements.empty"),
        body: t("money.statements.emptyBody"),
      }}
    />
  );
}

const period = (s: Statement) => (s.from && s.to ? (s.from === s.to ? formatDate(s.from) : `${formatDate(s.from)} – ${formatDate(s.to)}`) : "");

const result = (s: Statement) => (s.error ? serverText(s.error) : t("money.loaded", { imported: s.imported, duplicates: s.duplicates }));

const statementRoute = getRouteApi("/app/money/bank/statements/$id");

/** Одна загрузка: откуда, за какой период, что пришло, и её платежи. */
export function StatementPage() {
  const { id } = statementRoute.useParams();
  const statement = useQuery({
    queryKey: [...MONEY, "statement", id],
    queryFn: () => docsApi.statement(Number(id)),
  });
  const rows = useQuery({
    queryKey: [...MONEY, "statement", id, "payments"],
    queryFn: () => docsApi.payments("", null, { cursor: null, limit: 200 }, Number(id)),
  });
  const accounts = useQuery(accountsQuery);
  const navigate = useNavigate();
  const s = statement.data;
  return (
    <div className="page money">
      <BackHead to="/money/bank/statements" title={s ? `${accountName(accounts.data, s.account)} · ${formatDateTime(s.createdAt)}` : t("money.statement")} />
      {statement.error ? <Notice tone="danger" title={errorNotice(statement.error).title} /> : null}
      {s ? (
        <Plate cut="lg">
          <dl className="money-facts">
            <Fact label={t("money.source")}>{t(s.source === "API" ? "bank.api" : "bank.file")}</Fact>
            {period(s) ? <Fact label={t("money.period")}>{period(s)}</Fact> : null}
            <Fact label={t("money.result")}>{result(s)}</Fact>
            {s.createdBy ? <Fact label={t("money.loadedBy")}>{s.createdBy}</Fact> : <Fact label={t("money.loadedBy")}>{t("money.bySchedule")}</Fact>}
          </dl>
        </Plate>
      ) : null}
      <ListTable<Transaction>
        label={t("bank.transactions")}
        rows={rows.data?.items ?? []}
        rowKey={(r) => String(r.id)}
        onRowClick={(r) =>
          void navigate({
            to: "/money/bank/payments/$id",
            params: { id: String(r.id) },
          })
        }
        empty={<p className="w-muted">{t("bank.transactions.empty")}</p>}
        columns={paymentColumns(accounts.data)}
      />
    </div>
  );
}

// --- общее -------------------------------------------------------------------

export function BackHead({ to, title }: { to: "/money/bank/payments" | "/money/bank/statements" | "/money/opex/entries"; title: string }) {
  return (
    <div className="page-head">
      <Button asChild variant="ghost" size="icon">
        <Link to={to} aria-label={t("docs.back")}>
          <IconArrowLeft />
        </Link>
      </Button>
      <h1 className="page-title">{title}</h1>
    </div>
  );
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="money-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
